import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../../activities/types";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import type { PlanTag } from "../../../plans/PlanModel";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../../core/BaseModuleDataModel";
import CheckboxModuleModel from "../CheckboxModuleModel";
import { CheckboxUI } from "./CheckboxUI";

export interface CheckboxModelData extends BaseModuleModelData {
  indented: number;
  checked: boolean;
  label: string;
  assignedTo: string[];
  tags: string[];
}

export default class CheckboxModel extends BaseModuleDataModel<CheckboxModelData> {
  constructor(stores: Stores, api: Api, data: CheckboxModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      indented: data.indented ?? 0,
      checked: data.checked ?? false,
      assignedTo: data.assignedTo ?? [],
      tags: data.tags ?? [],
    });

    makeObservable(this);
  }

  @computed get useTags(): boolean {
    const m = this.module;
    if (m instanceof CheckboxModuleModel) {
      return m.data.settings.useTags;
    }
    return false;
  }

  @action toggleChecked() {
    this.data.checked = !this.data.checked;

    this.updateTimestamp();
    this.api.modulesDataApi.set(this);

    const m = this.module;
    if (m instanceof CheckboxModuleModel) {
      m.updateSnapshotWithoutSave();
      m.save();

      m.postActivityEvent(
        ActivityEventType.DataChanged,
        this.data.checked
          ? TKey.ActivityCheckboxCompleted
          : TKey.ActivityCheckboxReset,
        { label: this.data.label },
      );
      this.sendTelemetryEvent(this.data.checked ? "checked" : "not_checked");
    }
  }

  @action setLabel(newLabel: string, _pushChange?: boolean) {
    const oldLabel = this.data.label;

    if (oldLabel !== newLabel) {
      this.data.label = newLabel;

      this.updateTimestamp();
      this.api.modulesDataApi.set(this);

      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityCheckboxLabelUpdated,
        {
          oldValue: oldLabel,
          newValue: newLabel,
        },
      );
      this.sendTelemetryEvent("updated_label");
    }
  }

  @computed get planTags(): PlanTag[] {
    const tags: PlanTag[] = [];

    this.data.tags.forEach((tagUid) => {
      const foundTag = this.plan.getTag(tagUid);
      if (foundTag) {
        tags.push(foundTag);
      }
    });

    return tags;
  }

  @action setIndent(newIndent: number) {
    this.data.indented = newIndent;
    this.api.modulesDataApi.set(this);
  }

  @action toggleTag(tag: PlanTag) {
    if (!this.data.tags.includes(tag.uid)) {
      this.data.tags.push(tag.uid);
      this.sendTelemetryEvent("tag_used");
    } else {
      this.data.tags = this.data.tags.filter((tagUid) => tagUid !== tag.uid);
      this.sendTelemetryEvent("tag_removed");
    }

    // Filter out archived tags
    const archivedTags = this.planTags
      .filter((t) => t.archived)
      .map((t) => t.uid);
    if (archivedTags.length > 0) {
      this.data.tags = this.data.tags.filter(
        (tagUid) => !archivedTags.includes(tagUid),
      );
    }

    this.api.modulesDataApi.set(this);
  }

  @action toggleAssignee(userUid: string) {
    const displayName =
      this.stores.usersStore.byId.get(userUid)?.data.displayName;

    if (this.data.assignedTo.includes(userUid)) {
      this.data.assignedTo = this.data.assignedTo.filter((a) => a !== userUid);

      if (displayName) {
        this.module.postActivityEvent(
          ActivityEventType.DataChanged,
          TKey.ActivityCheckboxAssignedRemoved,
          {
            label: this.data.label,
            displayName,
          },
        );
      }
    } else {
      this.data.assignedTo.push(userUid);

      if (displayName) {
        this.module.postActivityEvent(
          ActivityEventType.DataChanged,
          TKey.ActivityCheckboxAssignedTo,
          {
            label: this.data.label,
            displayName,
          },
        );
      }
    }

    this.api.modulesDataApi.set(this);
    this.sendTelemetryEvent("updated_assignee");
  }

  @computed get assignees() {
    return this.data.assignedTo
      ?.map((assignedToUserUid) => {
        const user = this.stores.usersStore.byId.get(assignedToUserUid);

        return user?.data.displayName || "Uknown";
      })
      .join(", ");
  }

  toFireStore(): Omit<CheckboxModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <CheckboxUI key={this.uid} data={this} />;
  }
}
